import React from 'react';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { Scale, ChevronRight, FileCheck2, FileLock2 } from 'lucide-react';
import { Link } from 'react-router';
import { TenantMenuItemsExternalInner } from '@askporter/client-grieg-lyric';
import { useConfig } from '@askporter/config-provider';
import { IconFolder } from '@askporter/utils';
import { Icon, IconSize } from '../../../Icon';
import { Typography } from '../../../Typography';
import { disabledNavStyle, IconLink, NavbarLink } from '../../utils';
interface AccountMenuProps extends MenuProps {
  t: (key: string) => string;
  handleSignOut: () => void;
  mainItems: Array<IconLink | NavbarLink>;
  tenantNavButtons: TenantMenuItemsExternalInner[];
}
/** Renders a AccountMenu component */
const AccountMenu: React.FC<React.PropsWithChildren<AccountMenuProps>> = ({
  t,
  handleSignOut,
  mainItems,
  tenantNavButtons = [],
  ...menuProps
}: AccountMenuProps) => {
  const config = useConfig();
  const [legalAnchorEl, setLegalAnchorEl] = React.useState<null | HTMLElement>(null);
  const legalMenuItemRef = React.useRef<HTMLLIElement>(null);
  const { isDisabled, disabledNavStyles } = disabledNavStyle(location.pathname, '/app/account');
  const isSubmenuOpen = Boolean(legalAnchorEl);
  const openSubmenu = () => setLegalAnchorEl(legalMenuItemRef.current);
  const closeSubmenu = (restoreFocus = true) => {
    setLegalAnchorEl(null);
    if (restoreFocus) legalMenuItemRef.current?.focus();
  };
  const handleLegalKeyDown = (e: React.KeyboardEvent) => {
    if (['Enter', ' ', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      openSubmenu();
    }
  };
  const handleSubmenuKeyDown = (e: React.KeyboardEvent) => {
    if (['Escape', 'ArrowLeft'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      closeSubmenu();
    } else if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      // Stop propagation to prevent main menu from handling these
      e.stopPropagation();
    }
  };
  return (
    <>
      <Menu data-testid="menu" keepMounted {...menuProps}>
        <MenuItem
          to="/app/account"
          component={Link}
          state={{ from: location.pathname }}
          disabled={isDisabled}
          sx={{ ...disabledNavStyles }}
          data-testid="my-account-btn"
          divider={true}
        >
          <Icon size={IconSize.MD} folder={'design-system/user'} />
          <Typography ml={2} variant="body2">
            {t('ns.global:header:nav:my_account')}
          </Typography>
        </MenuItem>
        {mainItems.map((item, idx) => {
          if ('href' in item) {
            const { isDisabled, disabledNavStyles } = disabledNavStyle(location.pathname, item?.href);
            return (
              <MenuItem
                component={Link}
                to={item?.href}
                key={`${item?.href}_${item?.translation}`}
                sx={{
                  ...disabledNavStyles,
                }}
                disabled={isDisabled}
                divider={item.href === '/app/admin-config' || item.href === '/app/exports'}
              >
                <Icon size={IconSize.MD} folder={item?.iconFolder} />
                <Typography ml={2} variant="body2">
                  {t(item?.translation)}
                </Typography>
              </MenuItem>
            );
          } else if ('hamburger' in item) return <Box key={`hamburger_item_${idx}`}>{item?.hamburger}</Box>;
        })}
        {tenantNavButtons?.map((item, idx) => {
          return (
            <MenuItem
              component={'a'}
              href={item?.linkAddress}
              key={idx}
              target={item?.openInNew ? '_blank' : undefined}
              divider={idx === tenantNavButtons.length - 1}
            >
              <Icon size={IconSize.MD} folder={item?.iconPath as IconFolder} />
              <Typography ml={2} variant="body2">
                {item?.linkTitle}
              </Typography>
            </MenuItem>
          );
        })}
        <MenuItem
          ref={legalMenuItemRef}
          aria-label={t('ns.global:header:nav:legal')}
          aria-haspopup="menu"
          aria-expanded={isSubmenuOpen}
          aria-controls={isSubmenuOpen ? 'legal-submenu' : undefined}
          data-testid="legal"
          sx={{ width: '200px' }}
          divider={true}
          selected={isSubmenuOpen}
          onMouseEnter={openSubmenu}
          onMouseLeave={() => closeSubmenu(false)}
          onKeyDown={handleLegalKeyDown}
        >
          <ListItemIcon sx={{ minWidth: '24px !important' }}>
            <Scale size={20} color="#A4A7AE" />
          </ListItemIcon>
          <Typography ml={2} variant="body2">
            {t('ns.global:header:nav:legal')}
          </Typography>
          <ListItemIcon sx={{ minWidth: '24px !important', marginLeft: 'auto' }}>
            <ChevronRight size={20} color="#A4A7AE" />
          </ListItemIcon>
        </MenuItem>
        <Popper open={isSubmenuOpen} anchorEl={legalAnchorEl} placement="left-start" sx={{ zIndex: 1301 }}>
          <Paper
            elevation={3}
            onMouseEnter={openSubmenu}
            onMouseLeave={() => closeSubmenu(false)}
            sx={{
              minWidth: 200,
              borderRadius: 2,
              '& .MuiMenuItem-root': { py: 1.5, px: 2 },
            }}
          >
            <MenuList id="legal-submenu" autoFocusItem={isSubmenuOpen} onKeyDown={handleSubmenuKeyDown}>
              <MenuItem
                onClick={() => {
                  setLegalAnchorEl(null);
                  window.open(config.tenantDisplay?.links?.termsOfUseUrl, '_blank');
                }}
              >
                <ListItemIcon sx={{ minWidth: '24px !important' }}>
                  <FileCheck2 color="#A4A7AE" size={16} />
                </ListItemIcon>
                <Typography variant="body2">{t('ns.global:header:nav:terms')}</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setLegalAnchorEl(null);
                  window.open(config.tenantDisplay?.links?.privacyPolicyUrl, '_blank');
                }}
              >
                <ListItemIcon sx={{ minWidth: '24px !important' }}>
                  <FileLock2 color="#A4A7AE" size={16} />
                </ListItemIcon>
                <Typography variant="body2">{t('ns.global:header:nav:policy')}</Typography>
              </MenuItem>
            </MenuList>
          </Paper>
        </Popper>
        <MenuItem
          aria-label={t('ns.common:sign_out:button')}
          data-testid="sign-out"
          onClick={handleSignOut}
          sx={{ width: '200px' }}
        >
          <Icon size={IconSize.MD} folder="design-system/logout" />
          <Typography ml={2} variant="body2">
            {t('ns.global:header:nav:logout')}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
export default AccountMenu;
